// Super Admin — SMTP Master Settings (Gmail + generic SMTP)
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Save, Send, CheckCircle2, AlertTriangle, KeyRound } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";

export function AdminSMTPSettingsPage() {
  const [s, setS] = useState(null);
  const [pw, setPw] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/smtp-settings");
      setS(data.data);
      if (data.data?.smtp_user && !testEmail) setTestEmail(data.data.smtp_user);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        smtp_host: s.smtp_host, smtp_port: parseInt(s.smtp_port || 587, 10),
        smtp_user: s.smtp_user,
        smtp_from_email: s.smtp_from_email, smtp_from_name: s.smtp_from_name,
        smtp_enabled: s.smtp_enabled, smtp_use_tls: s.smtp_use_tls,
      };
      if (pw.trim()) payload.smtp_password = pw.trim();
      await api.put("/admin/smtp-settings", payload);
      setPw("");
      toast.success("SMTP settings saved");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    if (!testEmail.match(/^\S+@\S+\.\S+$/)) return toast.error("Enter a valid test-to email");
    setTesting(true);
    try {
      const { data } = await api.post("/admin/smtp-settings/test", { to_email: testEmail });
      toast.success(`Test email ${data.data?.status || "sent"} · ${data.data?.message_id?.slice(0, 20) || ""}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTesting(false); }
  };

  if (!s) return <DashboardLayout title="SMTP Settings"><LoadingBlock /></DashboardLayout>;

  const configured = s.password_configured;
  return (
    <DashboardLayout title="SMTP Master Settings" breadcrumbs={["Admin", "Master", "SMTP"]}>
      <div className="max-w-2xl space-y-4">
        <div className={`rounded-2xl border p-4 ${configured ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`} data-testid="smtp-status">
          <div className="flex items-center gap-3">
            {configured ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
            <div>
              <div className="text-sm font-bold text-slate-800">
                {configured ? "SMTP is configured" : "SMTP password not set"}
              </div>
              <div className="text-xs text-slate-600">
                Source: <span className="font-semibold">{s.source}</span>{configured && ` · Password: ${s.smtp_password_masked}`}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="smtp-form">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <Mail className="h-4 w-4 text-[#101876]" />
            <div className="text-sm font-black text-slate-900">Configuration</div>
          </div>
          <label className="flex items-center justify-between rounded-lg p-2 hover:bg-slate-50 cursor-pointer">
            <span className="text-sm">SMTP delivery enabled</span>
            <input type="checkbox" checked={s.smtp_enabled} onChange={() => setS({ ...s, smtp_enabled: !s.smtp_enabled })} className="h-4 w-4" data-testid="smtp-enabled" />
          </label>
          <label className="flex items-center justify-between rounded-lg p-2 hover:bg-slate-50 cursor-pointer">
            <span className="text-sm">Use STARTTLS (required for Gmail port 587)</span>
            <input type="checkbox" checked={s.smtp_use_tls} onChange={() => setS({ ...s, smtp_use_tls: !s.smtp_use_tls })} className="h-4 w-4" data-testid="smtp-tls" />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="SMTP host" value={s.smtp_host} onChange={(e) => setS({ ...s, smtp_host: e.target.value })} testId="smtp-host" placeholder="smtp.gmail.com" />
            <Field label="SMTP port" type="number" value={s.smtp_port} onChange={(e) => setS({ ...s, smtp_port: e.target.value })} testId="smtp-port" />
            <Field label="SMTP username (Gmail address)" value={s.smtp_user} onChange={(e) => setS({ ...s, smtp_user: e.target.value })} testId="smtp-user" placeholder="you@gmail.com" />
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                <KeyRound className="inline h-3 w-3 mr-1" />App Password
                {configured && <span className="ml-2 text-slate-400 font-normal">(leave blank to keep {s.smtp_password_masked})</span>}
              </label>
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                     data-testid="smtp-password"
                     placeholder="16-char Google App Password"
                     className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-mono focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20" />
            </div>
            <Field label="From email" value={s.smtp_from_email} onChange={(e) => setS({ ...s, smtp_from_email: e.target.value })} testId="smtp-from-email" />
            <Field label="From name" value={s.smtp_from_name} onChange={(e) => setS({ ...s, smtp_from_name: e.target.value })} testId="smtp-from-name" />
          </div>

          <details className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <summary className="cursor-pointer font-semibold text-slate-800">How to get a Gmail App Password</summary>
            <ol className="mt-2 ml-4 list-decimal space-y-1">
              <li>Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="text-[#101876] hover:underline">myaccount.google.com/security</a></li>
              <li>Enable <b>2-Step Verification</b> (mandatory)</li>
              <li>Open <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[#101876] hover:underline">App passwords</a></li>
              <li>Choose "Mail" and name it e.g. "Steward Learning"</li>
              <li>Paste the 16-character code above (spaces will be auto-stripped)</li>
            </ol>
          </details>

          <button type="button" onClick={save} disabled={busy} data-testid="smtp-save"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#101876] py-3 text-sm font-bold text-white hover:bg-[#0d1360] disabled:opacity-70">
            <Save className="h-4 w-4" />{busy ? "Saving…" : "Save SMTP settings"}
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
          <div className="text-sm font-black text-slate-900">Send a test email</div>
          <div className="flex gap-2">
            <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@yourdomain.com" data-testid="smtp-test-to"
                   className="flex-1 rounded-xl border border-slate-300 px-3 py-2.5 text-sm" />
            <button type="button" onClick={test} disabled={testing || !configured} data-testid="smtp-test-send"
                    className="rounded-xl bg-[#329112] px-4 text-sm font-bold text-white hover:bg-[#26720d] disabled:opacity-50 disabled:cursor-not-allowed">
              <Send className="inline h-4 w-4 mr-1" />{testing ? "Sending…" : "Send test"}
            </button>
          </div>
          {!configured && <p className="text-xs text-amber-700">Configure the password + save before testing.</p>}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, testId, type = "text", ...rest }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-700 mb-1">{label}</label>
      <input type={type} {...rest} data-testid={testId}
             className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20" />
    </div>
  );
}
